/**
 * App Navigation Hook — extracted from App.tsx
 *
 * Manages navigation state, view routing, cross-portal navigation,
 * and account switching (navigation side-effects).
 *
 * @moved from src/hooks/useAppNavigation.ts (B1 — DDD domain move to app/)
 *
 * Boot Flow Refactor (mei 2026):
 * - showWelcome is now derived synchronously from store state on first render,
 *   eliminating the 1-frame flash where AppContent rendered before WelcomeScreen.
 * - Removed auto-load-demo behaviour: empty state now shows WelcomeScreen so
 *   the user makes a conscious choice (Demo / Login / Register).
 */

import { useState, useEffect, useCallback } from 'react'
import { type AccountsState, type Account } from '../../accounts'
import { logout as supabaseLogout } from '../../data/supabase/authService'
import {
  SUPABASE_AUTH_ENABLED,
  getActiveProfile,
} from '../../data/supabase/session'
import {
  pushSoloProgress,
  resetSyncRevision,
} from '../../data/supabase/soloProgressSync'
import { useStore } from '../store'

export type AppView = 'dashboard' | 'practice' | 'assessment-session'

export type NavView =
  | 'home'
  | 'modules'
  | 'progress'
  | 'collection'
  | 'settings'
  | 'assessment'
  | 'assessmentReport'
  | 'parent'
  | 'teacher'
  | 'learningLab'
  | 'admin'
  | 'login'
  | 'register'

interface UseAppNavigationParams {
  accounts: AccountsState
  activeAccount: Account | null
  isInitialized: boolean
  switchAccount: (accountId: string) => void
  setViewingLearner: (learnerId: string | null) => void
  loadDemoAccounts: () => Promise<void> | void
  clearActiveAccount: () => void
}

/**
 * Synchronously derive whether the WelcomeScreen should show, based on the
 * current store state. This avoids the 1-frame flash where the learner UI
 * briefly rendered before a useEffect flipped showWelcome to true.
 */
function deriveInitialShowWelcome(
  accounts: AccountsState,
  isInitialized: boolean,
): boolean {
  if (!isInitialized) {
    // Pre-init: default to welcome so we never flash AppContent first
    return true
  }
  const accountList = Object.values(accounts.accountsById)
  if (accountList.length === 0) return true
  if (!accounts.activeAccountId) return true
  return false
}

export function useAppNavigation({
  accounts,
  activeAccount,
  isInitialized,
  switchAccount,
  setViewingLearner,
  loadDemoAccounts,
  clearActiveAccount,
}: UseAppNavigationParams) {
  const [currentView, setCurrentView] = useState<AppView>('dashboard')
  const [currentNavView, setCurrentNavView] = useState<NavView>('home')
  const [previousNavView, setPreviousNavView] = useState<NavView | null>(null)

  // Initialize from current store state synchronously — no flash.
  const [showWelcome, setShowWelcome] = useState<boolean>(() =>
    deriveInitialShowWelcome(accounts, isInitialized),
  )

  // Once the store finishes hydrating, re-derive showWelcome from the
  // canonical store state. Crucial: we must also flip welcome → false for
  // returning users (active account already in localStorage). Previously
  // this effect only ever set true, leaving returning users stuck on the
  // welcome screen. Note: we no longer auto-load demo accounts on empty
  // state — first visit shows WelcomeScreen so the user explicitly picks.
  useEffect(() => {
    if (!isInitialized) return
    setShowWelcome(deriveInitialShowWelcome(accounts, true))
  }, [isInitialized, accounts.activeAccountId, accounts.accountsById])

  // Account switching with navigation routing
  const handleSwitchAccount = useCallback(
    (accountId: string) => {
      switchAccount(accountId)
      setPreviousNavView(null)
      const newAccount = accounts.accountsById[accountId]
      if (newAccount) {
        switch (newAccount.role) {
          case 'learner':
            setCurrentNavView('home')
            break
          case 'parent':
            setCurrentNavView('parent')
            break
          case 'teacher':
            setCurrentNavView('teacher')
            break
          case 'admin':
            setCurrentNavView('admin')
            break
        }
      }
    },
    [switchAccount, accounts.accountsById],
  )

  const [isLoadingDemo, setIsLoadingDemo] = useState(false)

  const handleLoadDemoAccounts = useCallback(async () => {
    // Await content + demo seeding BEFORE flipping the welcome screen off.
    // Otherwise AppContent would render with empty accounts/questions for
    // the brief moment between click and demo state landing in the store.
    setIsLoadingDemo(true)
    try {
      await loadDemoAccounts()
      setShowWelcome(false)
      setCurrentNavView('home')
    } finally {
      setIsLoadingDemo(false)
    }
  }, [loadDemoAccounts])

  // Cross-Portal Navigation
  const handleNavigateToLearner = useCallback(
    (learnerId: string) => {
      setViewingLearner(learnerId)
      setPreviousNavView(currentNavView)
      setCurrentNavView('home')
    },
    [setViewingLearner, currentNavView],
  )

  const handleReturnToPortal = useCallback(() => {
    setViewingLearner(null)
    if (previousNavView) {
      setCurrentNavView(previousNavView)
      setPreviousNavView(null)
    } else {
      if (activeAccount?.role === 'parent') setCurrentNavView('parent')
      else if (activeAccount?.role === 'teacher') setCurrentNavView('teacher')
      else if (activeAccount?.role === 'admin') setCurrentNavView('admin')
      else setCurrentNavView('home')
    }
  }, [setViewingLearner, previousNavView, activeAccount?.role])

  const handleLogout = useCallback(() => {
    // EERST de wachtende synchronisatie afdwingen. Het wegschrijven wacht vijf
    // seconden na de laatste wijziging; wie binnen dat venster uitlogt — en dat
    // is precies wat een kind doet dat klaar is — verliest anders zijn laatste
    // opgaven. Op een gedeeld apparaat waar daarna een broertje inlogt, komt
    // dat nooit meer goed.
    //
    // Bewust NIET afwachten: uitloggen mag nooit blijven hangen op een trage
    // verbinding. Mislukt de verzending, dan staat de voortgang nog lokaal.
    if (SUPABASE_AUTH_ENABLED) {
      const profile = getActiveProfile()
      if (profile?.role === 'learner') {
        const local =
          useStore.getState().multiAccountState?.learnerData?.[profile.id]
        if (local) void pushSoloProgress(local as never)
      }
      // De revisie hoort bij de sessie die we nu beëindigen. Blijft hij staan,
      // dan botst de volgende gebruiker op een conflict dat hij niet begrijpt.
      resetSyncRevision()
    }

    // Lokaal METEEN leegmaken, niet pas na het serverantwoord. Andersom zou een
    // trage of mislukte signOut betekenen dat een kind na het klikken op
    // "uitloggen" nog steeds zijn gegevens op het scherm heeft — op een gedeeld
    // klaslokaal-apparaat precies wat je niet wilt.
    clearActiveAccount()
    setViewingLearner(null)
    setShowWelcome(true)
    setCurrentNavView('home')

    // Daarna pas de sessie bij Supabase intrekken. Zonder deze aanroep bleef
    // het token gewoon geldig: lokaal uitgelogd, maar op de server nog niet.
    // Mislukt het (offline), dan is de gebruiker lokaal hoe dan ook uit en
    // verloopt het token vanzelf.
    if (SUPABASE_AUTH_ENABLED) {
      void supabaseLogout()
    }
  }, [clearActiveAccount, setViewingLearner])

  // Navigate to login screen (from welcome or test button)
  const handleNavigateToLogin = useCallback(() => {
    setShowWelcome(false)
    setCurrentNavView('login')
  }, [])

  // Navigate to register screen
  const handleNavigateToRegister = useCallback(() => {
    setShowWelcome(false)
    setCurrentNavView('register')
  }, [])

  // Handle successful login — route to correct portal based on role
  const handleLoginSuccess = useCallback(
    (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => {
      switchAccount(accountId)
      setShowWelcome(false)
      const accountRole = role || accounts.accountsById[accountId]?.role
      switch (accountRole) {
        case 'learner':
          setCurrentNavView('home')
          break
        case 'parent':
          setCurrentNavView('parent')
          break
        case 'teacher':
          setCurrentNavView('teacher')
          break
        case 'admin':
          setCurrentNavView('admin')
          break
        default:
          setCurrentNavView('home')
      }
    },
    [switchAccount, accounts.accountsById],
  )

  const resetToDashboard = useCallback(() => {
    setCurrentView('dashboard')
  }, [])

  const resetToLearningLab = useCallback(() => {
    setCurrentView('dashboard')
    setCurrentNavView('learningLab')
  }, [])

  const enterPracticeView = useCallback(() => {
    setCurrentView('practice')
  }, [])

  return {
    currentView,
    setCurrentView,
    currentNavView,
    setCurrentNavView,
    showWelcome,
    setShowWelcome,
    isLoadingDemo,
    handleSwitchAccount,
    handleLoadDemoAccounts,
    handleNavigateToLearner,
    handleReturnToPortal,
    handleLogout,
    handleNavigateToLogin,
    handleNavigateToRegister,
    handleLoginSuccess,
    resetToDashboard,
    resetToLearningLab,
    enterPracticeView,
  }
}
